import {
  Image as ChakraUIImage,
  ImageProps as ChakraUIImageProps,
  Box,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Flex,
  ModalHeader
} from "@chakra-ui/react";
import { MouseEvent, useState } from "react";

import { Header } from "../contribution/header/header";

export interface ImageProps extends ChakraUIImageProps {
  magnify?: boolean;
  viewWithHeader?: boolean;
  username?: string;
  userAvatarSrc?: string;
  contributionTime?: string;
  isLoaded?: boolean;
  magnifiedImage?: boolean;
  isCursor?: boolean;
}

export interface ImageViewerProps extends ImageProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
  handleClick: (e: MouseEvent<HTMLElement>) => void;
  username?: string;
  userAvatarSrc?: string;
  contributionTime?: string;
  isLoaded?: boolean;
  isCursor?: boolean;
}

export interface ImageComponentProps extends ChakraUIImageProps {
  handleClick: (e: MouseEvent<HTMLElement>) => void;
  isCursor?: boolean;
  magnifiedImage?: boolean;
}

const ImageComponent = ({
  handleClick,
  isCursor,
  magnifiedImage,
  ...rest
}: ImageComponentProps) => {
  return (
    <Box
      mr={2}
      pt={1}
      as="span"
      opacity={1}
      minW="100px"
      cursor={magnifiedImage ? "zoom-in" : isCursor ? "pointer" : "default"}
      onClick={handleClick}
      _hover={{ opacity: 0.7 }}
      borderRadius="lg"
    >
      <ChakraUIImage borderRadius="lg" objectFit="cover" {...rest} />
    </Box>
  );
};

const ImageViewerWithoutHeader = (props: ImageViewerProps) => {
  const { handleClick, setOpen, open, isCursor, ...rest } = props;

  return (
    <>
      <ImageComponent handleClick={handleClick} isCursor={isCursor} {...rest} />
      <Modal size="xl" isOpen={open} onClose={() => setOpen(false)}>
        <ModalOverlay />
        <ModalContent width="auto">
          <ModalBody p={0}>
            <ChakraUIImage
              width="auto"
              height="auto"
              objectFit="cover"
              {...rest}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const ImageViewerWithHeader = (props: ImageViewerProps) => {
  const {
    handleClick,
    setOpen,
    open,
    username,
    userAvatarSrc,
    contributionTime,
    isLoaded,
    magnifiedImage,
    isCursor,
    ...rest
  } = props;
  return (
    <>
      <ImageComponent
        handleClick={handleClick}
        magnifiedImage={magnifiedImage}
        isCursor={isCursor}
        {...rest}
      />
      <Modal
        size={{ base: "full", lg: "xl" }}
        isOpen={open}
        onClose={() => setOpen(false)}
        isCentered
      >
        <ModalOverlay bgColor="black" color="white" />
        <ModalContent bgColor="black" color="white">
          <ModalHeader
            display="flex"
            p={3}
            alignItems="center"
            justifyContent="space-between"
          >
            <Header
              contributionTime={contributionTime}
              userAvatarSrc={userAvatarSrc}
              username={username}
              showCardBackground={true}
              showHeaderColor={true}
              switchHeaderDateColor
              isLoaded={isLoaded}
              switchOnSquareCard={true}
            />
            <ModalCloseButton position="static" />
          </ModalHeader>
          <ModalBody bgColor="black" color="white" p={0}>
            <Flex
              p={4}
              alignItems="center"
              justifyContent="center"
              height={{ base: "calc(100vh - 80px)", lg: "auto" }}
            >
              <ChakraUIImage
                width="100%"
                height="auto"
                maxHeight="456px"
                marginLeft="auto"
                marginRight="auto"
                {...rest}
                objectFit="contain"
              />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export function Image({
  magnify,
  viewWithHeader,
  username,
  userAvatarSrc,
  contributionTime,
  isLoaded,
  magnifiedImage,
  isCursor,
  ...rest
}: ImageProps) {
  const [open, setOpen] = useState(false);

  const handleClick = (e: MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    setOpen(true);
  };

  if (magnify && viewWithHeader) {
    return (
      <ImageViewerWithHeader
        setOpen={setOpen}
        open={open}
        handleClick={handleClick}
        contributionTime={contributionTime}
        userAvatarSrc={userAvatarSrc}
        username={username}
        isLoaded={isLoaded}
        magnify={magnify}
        magnifiedImage={magnifiedImage}
        isCursor={isCursor}
        {...rest}
      />
    );
  }

  if (magnify) {
    return (
      <ImageViewerWithoutHeader
        setOpen={setOpen}
        open={open}
        handleClick={handleClick}
        contributionTime={contributionTime}
        userAvatarSrc={userAvatarSrc}
        username={username}
        isLoaded={isLoaded}
        isCursor={isCursor}
        {...rest}
      />
    );
  }

  return <ChakraUIImage borderRadius="lg" objectFit="cover" {...rest} />;
}
